import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './OnDevice.css';
import { useConfig } from '../ConfigContext';

// Reusable icon placeholder — matches Cloud page style
function IconPlaceholder({ size = 40, label = '' }) {
  return (
    <div
      className="od-icon-placeholder"
      style={{ width: size, height: size, minWidth: size }}
      aria-label={label}
    >
      <span className="od-icon-ph-label">{label}</span>
    </div>
  );
}

function OnDevice() {
  const config = useConfig();
  const od = config.onDevicePage ?? {};
  const showPH = od.showPlaceholders ?? true;
  const [expandedPlatform, setExpandedPlatform] = useState(null);

  const platforms = (od.platforms ?? []).length > 0
    ? od.platforms.map((p, i) => ({
        id: `platform-${i}`,
        name: p.name,
        status: 'available',
        modeLabel: i === 0 ? 'Fully Offline' : 'Offline or Cloud',
        mode: i === 0 ? 'offline' : 'hybrid',
        iconLabel: p.name,
        icon: p.icon ?? null,
        description: p.desc,
        specs: p.specs ?? [],
        accent: i % 2 === 0 ? 'teal' : 'blue',
      }))
    : [
    {
      id: 'raspberry-pi',
      icon: '/icons/ondevice/raspberry-pi.png',
      name: 'Raspberry Pi',
      status: 'available',
      mode: 'offline',
      modeLabel: 'Fully Offline',
      iconLabel: 'Pi',
      description: 'The complete Tytonvoice pipeline — VAD, detection, and transcription — runs entirely on-chip. No network required. Proven in live demos with Pryme keyword detection at under 50ms latency on Pi 4 hardware.',
      specs: [
        'Tested on Raspberry Pi 4 (2GB+)',
        'CPU-only inference, no GPU required',
        'Under 50ms end-to-end detection latency',
        'Full offline operation — zero network dependency',
        'Both Pryme and Apex SDK supported',
      ],
      accent: 'teal',
    },
    {
      id: 'android',
      icon: '/icons/ondevice/android.png',
      name: 'Android',
      status: 'available',
      mode: 'hybrid',
      modeLabel: 'Offline or Cloud',
      iconLabel: 'Android',
      description: 'Native Android SDK built in Kotlin. Run models fully on-device with OFFLINE=true for maximum privacy, or connect to your cloud backend for larger model variants. Supports both Pryme and Apex.',
      specs: [
        'Native Kotlin SDK — Android Studio integration',
        'OFFLINE=true for fully local inference',
        'Hybrid mode for cloud-backed larger models',
        'Supports ARMv8 and x86_64 architectures',
        'Background service and foreground mic support',
      ],
      accent: 'blue',
    },
    {
      id: 'windows',
      icon: '/icons/ondevice/windows.png',
      name: 'Windows',
      status: 'available',
      mode: 'hybrid',
      modeLabel: 'Offline or Cloud',
      iconLabel: 'Windows',
      description: 'Python SDK runs locally on any Windows machine with OFFLINE=true — no network, no cloud dependency. Full pipeline on CPU. Cloud mode available for teams wanting centralized model management.',
      specs: [
        'Python SDK — pip install tytonvoice',
        'OFFLINE=true for fully local operation',
        'CPU-optimized — runs on standard hardware',
        'Windows 10 and 11 supported',
        'Live mic and audio file input',
      ],
      accent: 'teal',
    },
    {
      id: 'linux',
      icon: '/icons/ondevice/linux.png',
      name: 'Linux',
      status: 'available',
      mode: 'hybrid',
      modeLabel: 'Offline or Cloud',
      iconLabel: 'Linux',
      description: 'The primary development and deployment platform for Tytonvoice. Full SDK support across distributions. Ideal for server-side edge nodes, industrial hardware, and air-gapped environments.',
      specs: [
        'Python SDK — pip install tytonvoice',
        'OFFLINE=true for air-gapped deployments',
        'Tested on Ubuntu, Debian, and Raspberry Pi OS',
        'x86_64 and ARM64 architectures',
        'Systemd service integration supported',
      ],
      accent: 'blue',
    },
  ];

  const comingSoon = [
    {
      id: 'ios',
      name: 'iOS',
      icon: '/icons/ondevice/ios.png',
      iconLabel: 'iOS',
      description: 'Native iOS SDK for on-device inference. Full offline operation with Core ML integration.',
    },
    {
      id: 'macos',
      name: 'macOS',
      icon: '/icons/ondevice/macos.png',
      iconLabel: 'macOS',
      description: 'Native macOS SDK with Apple Silicon optimization and Core ML acceleration.',
    },
  ];

  const privacyPoints = (od.privacyPoints ?? []).length > 0
    ? od.privacyPoints
    : [
    {
      title: 'Audio Never Leaves the Device',
      detail: 'With OFFLINE=true, every stage of the pipeline — voice activity detection, transcription, and keyword detection — runs locally. No audio is transmitted, buffered remotely, or logged externally.',
    },
    {
      title: 'Air-Gap Capable',
      detail: 'On-device deployment requires no internet connection at runtime. Models are bundled or pre-loaded at deploy time. Suitable for classified, healthcare, and industrial environments.',
    },
    {
      title: 'No Third-Party Dependencies at Runtime',
      detail: 'Once deployed, the SDK has no external API calls, no telemetry, and no cloud fallback unless explicitly configured. You control the full execution environment.',
    },
    {
      title: 'You Own the Model Weights',
      detail: 'Model weights are distributed with the SDK and remain on your hardware. No model-as-a-service dependency, no per-inference billing, no vendor lock-in.',
    },
  ];

  return (
    <div className="od-page">

      {/* ── HERO ── */}
      <section className="od-hero">
        <div className="od-hero-left">
          <div className="od-hero-badge">On-Device</div>
          <h1 className="od-hero-title">
            {od.heroTitle
              ? od.heroTitle.split('\n').map((line, i, arr) =>
                  i < arr.length - 1
                    ? <React.Fragment key={i}>{line}<br /></React.Fragment>
                    : <React.Fragment key={i}><span className="od-hero-title-accent">{line}</span></React.Fragment>
                )
              : (<>Voice Intelligence.<br />Runs on the Hardware.<br /><span className="od-hero-title-accent">Stays on the Hardware.</span></>)
            }
          </h1>
          <p className="od-hero-subtitle">
            {od.heroSubtitle ?? 'The complete Tytonvoice pipeline — VAD, keyword detection, and transcription — runs fully on-device with no network dependency. From Raspberry Pi to Android to production Linux hardware.'}
          </p>
          <div className="od-hero-cta-group">
            <a
              href={config.navbar.contactUs.url}
              target="_blank"
              rel="noopener noreferrer"
              className="od-btn-primary"
            >
              Contact Us
            </a>
            <Link to="/pryme#try-pryme-live" className="od-btn-secondary">
              Try Live Demo
            </Link>
          </div>
          <div className="od-hero-stats">
            {(od.heroStats ?? [
              { value: '0',     label: 'Network calls at runtime' },
              { value: '<50ms', label: 'Detection latency on Pi 4' },
              { value: '4',     label: 'Platforms available now' },
            ]).map((stat, i, arr) => (
              <React.Fragment key={i}>
                {i > 0 && <div className="od-stat-divider" />}
                <div className="od-stat">
                  <span className="od-stat-value">{stat.value}</span>
                  <span className="od-stat-label">{stat.label}</span>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
        <div className="od-hero-right">
          <div className={od.heroImage ? "od-hero-visual od-hero-visual-img" : "od-hero-visual"}>
            {od.heroImage
              ? <img src={od.heroImage} alt="On-Device Hero" style={{ width:'100%', height:'100%', objectFit:'cover', borderRadius:'16px', opacity: 0.15 }} />
              : showPH && (
                <>
                  <span className="od-ph-label">Hero Visual</span>
                  <span className="od-ph-sub">Device ecosystem — Pi, Android, embedded hardware</span>
                </>
              )
            }
          </div>
        </div>
      </section>

      {/* ── PROBLEM BAND ── */}
      <section className="od-problem">
        <div className="od-problem-inner">
          <p className="od-problem-text">
            Most voice AI requires a live cloud connection — your audio leaves the device,
            gets processed remotely, and comes back as text. That means latency, privacy exposure,
            and a hard dependency on network availability.{' '}
            <strong>On-device deployment eliminates all three.</strong>
          </p>
        </div>
      </section>

      {/* ── RASPBERRY PI SHOWCASE ── */}
      <section className="od-pi-showcase">
        <div className="od-pi-inner">
          <div className="od-pi-text">
            <div className="od-pi-eyebrow">Proven on hardware</div>
            <h2 className="od-pi-heading">Raspberry Pi Demo</h2>
            <p className="od-pi-desc">
              {od.piDesc ?? 'Pryme keyword detection running fully on-chip on a Raspberry Pi 4. No cloud connection. No GPU. Sub-50ms detection latency from spoken word to triggered event — on a $35 single-board computer.'}
            </p>
            <ul className="od-pi-specs">
              {(od.piSpecs ?? [
                'Raspberry Pi 4 (2GB RAM)',
                'CPU-only inference',
                'Full offline — no network',
              ]).map((spec, i) => <li key={i}>{spec}</li>)}
            </ul>
            <a
              href={config.navbar.contactUs.url}
              target="_blank"
              rel="noopener noreferrer"
              className="od-btn-primary"
            >
              Request Pi SDK Access
            </a>
          </div>
          <div className="od-pi-media">
            {od.piImage
              ? <img src={od.piImage} alt="Raspberry Pi Demo" className="od-pi-img" />
              : showPH && (
                <div className="od-pi-video-placeholder">
                  <div className="od-pi-play-btn">▶</div>
                  <span className="od-ph-label">Demo Video</span>
                  <span className="od-ph-sub">Raspberry Pi 4 — live keyword detection</span>
                </div>
              )
            }
          </div>
        </div>
      </section>

      {/* ── PLATFORM GRID ── */}
      <section className="od-platforms">
        <div className="od-section-header">
          <h2>Available Platforms</h2>
          <p className="od-section-subtitle">
            Every platform supports fully offline operation with optional cloud connectivity
          </p>
        </div>
        <div className="od-platform-grid">
          {platforms.map((p) => (
            <div
              key={p.id}
              className={`od-platform-card od-platform-${p.accent} ${expandedPlatform === p.id ? 'expanded' : ''}`}
              onClick={() => setExpandedPlatform(expandedPlatform === p.id ? null : p.id)}
            >
              <div className="od-platform-top">
                {p.icon
                  ? <img src={p.icon} alt={p.name} className="od-platform-icon" />
                  : <IconPlaceholder size={38} label={p.iconLabel} />
                }
                <div className="od-platform-title-group">
                  <h3 className="od-platform-name">{p.name}</h3>
                  <span className={`od-mode-badge od-mode-${p.mode}`}>{p.modeLabel}</span>
                </div>
                <span className="od-platform-caret">{expandedPlatform === p.id ? '▲' : '▼'}</span>
              </div>
              <p className="od-platform-desc">{p.description}</p>
              {expandedPlatform === p.id && (
                <ul className="od-platform-specs">
                  {p.specs.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* ── MODE EXPLAINER ── */}
      <section className="od-modes">
        <div className="od-section-header">
          <h2>Offline or Hybrid</h2>
          <p className="od-section-subtitle">
            Every platform gives you full control over where inference runs
          </p>
        </div>
        <div className="od-modes-grid">
          <div className="od-mode-card od-mode-card-offline">
            <div className="od-mode-card-header">
              <span className="od-mode-badge od-mode-offline">Fully Offline</span>
              <h3>OFFLINE=true</h3>
            </div>
            <p>
              Model weights are loaded locally. Every stage of the pipeline runs on the device.
              No network calls at runtime. Suitable for air-gapped systems, privacy-sensitive
              deployments, and anywhere network availability cannot be guaranteed.
            </p>
            <ul className="od-mode-features">
              <li>Zero network dependency at runtime</li>
              <li>Audio never transmitted externally</li>
              <li>Air-gap and classified environment ready</li>
              <li>No per-inference cost</li>
            </ul>
          </div>
          <div className="od-mode-card od-mode-card-hybrid">
            <div className="od-mode-card-header">
              <span className="od-mode-badge od-mode-hybrid">Hybrid</span>
              <h3>Cloud-Backed</h3>
            </div>
            <p>
              The client SDK handles audio capture and VAD on-device, then routes inference
              to your cloud backend. Enables larger, higher-accuracy models without local
              compute requirements. You control the backend infrastructure.
            </p>
            <ul className="od-mode-features">
              <li>Larger models for higher accuracy</li>
              <li>Centralized model management</li>
              <li>Same SDK API as offline mode</li>
              <li>Your infrastructure — your data</li>
            </ul>
          </div>
        </div>
      </section>

      {/* ── PRIVACY ARCHITECTURE ── */}
      <section className="od-privacy">
        <div className="od-section-header">
          <h2>Privacy by Architecture</h2>
          <p className="od-section-subtitle">
            On-device deployment isn't a feature — it's a fundamental design choice
          </p>
        </div>
        <div className="od-privacy-grid">
          {privacyPoints.map((pt, i) => (
            <div key={i} className="od-privacy-card">
              <div className="od-privacy-num">{String(i + 1).padStart(2, '0')}</div>
              <h3 className="od-privacy-title">{pt.title}</h3>
              <p className="od-privacy-detail">{pt.detail}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── COMING SOON ── */}
      <section className="od-coming-soon">
        <div className="od-section-header">
          <h2>Expanding Platform Support</h2>
          <p className="od-section-subtitle">
            More platforms and language bindings in active development
          </p>
        </div>
        <div className="od-soon-grid">
          {comingSoon.map((p) => (
            <div key={p.id} className={`od-soon-card ${p.highlight ? 'od-soon-highlight' : ''}`}>
              <div className="od-soon-top">
                {p.icon
                  ? <img src={p.icon} alt={p.name} className="od-platform-icon" />
                  : <IconPlaceholder size={38} label={p.iconLabel} />
                }
                <div>
                  <h3 className="od-soon-name">{p.name}</h3>
                  <span className="od-soon-badge">Coming Soon</span>
                </div>
              </div>
              <p className="od-soon-desc">{p.description}</p>

            </div>
          ))}
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="od-cta-section">
        <div className="od-cta-split">
          <div className="od-cta-left">
            <p className="od-cta-eyebrow">Deploy on your hardware</p>
            <div className="od-cta-heading">
              <div className="od-cta-brand">On-Device</div>
              <div className="od-cta-descriptor">Voice Intelligence</div>
            </div>
            <p className="od-cta-body">
              {od.ctaBody ?? 'From a $35 Raspberry Pi to enterprise Android deployments — the same SDK, the same accuracy, fully offline.'}
            </p>
            <a
              href={config.navbar.contactUs.url}
              target="_blank"
              rel="noopener noreferrer"
              className="od-btn-primary"
            >
              Contact Us
            </a>
          </div>
          <div className="od-cta-right">
            <ul className="od-cta-checklist">
              {(od.ctaChecklist ?? [
                'Fully offline with OFFLINE=true',
                'Audio never leaves the device',
                'Raspberry Pi, Android, Windows, Linux',
                'Same SDK API as cloud deployment',
                'iOS and macOS coming soon',
              ]).map((item, i) => (
                <li key={i} className="od-cta-check-item">
                  <span className="od-cta-check-icon">✓</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

    </div>
  );
}

export default OnDevice;